/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Tabs from "../../../../../components/Habitacional/Tabs";
import BabySteps from "../../../../../components/Habitacional/core/BabySteps";
import FormStepTwo from "./StepTwo/FormStepTwoMIP";
import FormStepOneCommunicatorMIP from "./StepOne/FormStepOneCommunicatorMIP";
import CompleteFormMIP from "./CompleteForm/CompleteFormMIP";
import DocumentPendent from "./DocumentPendent/DocumentPendent";
import Information from "../../../../../components/Habitacional/core/Information";
import FormStepOneInsuredMIP from "./StepOne/FormStepOneInsuredMIP";
import Response from "../../../../../components/Habitacional/core/Response/Response";
import CSLoading from "../../../../../components/common/CSLoading";
import { Steps as InitialStep } from "./utils/ConstantsFormMIP";

import useAuthToken from "../../../../../hooks/requests/useAuthToken";

import { envs } from "../../../../../config";
import { createPayloadByType } from "../utils/FormUtils";
import {
  SchemaFormMIPStepOneInsured,
  SchemaFormMIPStepOneCommunicator,
  SchemaFormMIPStepOneCommunicatorWithContact,
} from "./utils/SchemaFormMIP";

import * as S from "./FormMIP.Style";

const getResolver = ({
  currentStep,
  activeTabId,
  activeTabIdDataForContact,
}) => {
  if (currentStep?.id === 1 && activeTabId === 1) {
    return yupResolver(SchemaFormMIPStepOneInsured);
  }

  if (currentStep?.id === 1 && activeTabId === 2) {
    return activeTabIdDataForContact === 1
      ? yupResolver(SchemaFormMIPStepOneCommunicator)
      : yupResolver(SchemaFormMIPStepOneCommunicatorWithContact);
  }

  return () => ({ values: {}, errors: {} });
};

const FormMIP = () => {
  const [steps, setSteps] = useState(InitialStep);
  const [formData, setFormData] = useState({});
  const [activeTabId, setActiveTabId] = useState(1);
  const [activeTabIdDataForContact, setActiveTabIdDataForContact] = useState(1);
  const [allDocumentsSent, setAllDocumentsSent] = useState(false);
  const [comunicatorId, setComunicatorId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [failedRequest, setFailedRequest] = useState(false);

  const { refreshToken } = useAuthToken();

  const currentStep = steps.find((step) => step.step);

  const methods = useForm({
    resolver: getResolver({
      currentStep,
      activeTabId,
      activeTabIdDataForContact,
    }),
    defaultValues: formData,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = methods;

  const handleSteps = (id) => {
    const updatedSteps = steps.map((step) => ({
      ...step,
      step: step.id === id,
      completed: step.id < id,
    }));

    setSteps(updatedSteps);
  };

  const handleTabChange = (id) => {
    setActiveTabId(id);
  };

  const handleNextStep = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    const data = getValues();
    setFormData((prev) => ({ ...prev, ...data }));

    if (currentStep?.id === 1) {
      handleSteps(2);
      return;
    }

    setSteps(
      steps.map((step) => ({
        ...step,
        step: false,
        completed: true,
        pending: !allDocumentsSent ? step?.id === 2 : false,
      }))
    );
  };

  const handleUpdateSendDocuments = (allSent) => {
    setAllDocumentsSent(allSent);
  };

  const getTabItems = () => [
    {
      id: 1,
      mainTitleTab: "Segurado",
      content: (
        <>
          <S.TitleForm>Registre o pedido de indenização (sinistro)</S.TitleForm>
          <div style={{ gap: "32px" }}>
            <FormStepOneInsuredMIP
              control={control}
              errors={errors}
              isLoading={isLoading}
            />
          </div>
        </>
      ),
    },
    {
      id: 2,
      mainTitleTab: "Comunicante",
      content: (
        <>
          <S.TitleForm>Registre o pedido de indenização (sinistro)</S.TitleForm>
          <div style={{ gap: "32px" }}>
            <FormStepOneCommunicatorMIP
              control={control}
              errors={errors}
              setActiveTabIdDataForContact={setActiveTabIdDataForContact}
              isLoading={isLoading}
            />
          </div>
        </>
      ),
    },
  ];

  async function onSubmit() {
    setIsLoading(true);
    const data = getValues();
    const token = await refreshToken();

    const payload = createPayloadByType(data, "MIP");

    const myHeaders = new Headers();
    myHeaders.append("X-IBM-Client-Id", envs.clientId);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    try {
      const raw = JSON.stringify(payload);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
      };

      const url = `${envs.hostApic}/segurohabitacional/v1/sinistro/comunicados`;
      const response = await fetch(url, requestOptions);
      const json = await response.json();

      setComunicatorId(json?.response?.id);
      handleNextStep();
    } catch {
      setFailedRequest(true);
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <CSLoading center />;
  }

  return (
    <>
      {!failedRequest ? (
        <>
          <BabySteps Steps={steps} />
          <S.ContentTabsMIP noMargin={steps.every((step) => step.completed)}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {steps[0]?.step && (
                  <>
                    <S.TitleForm>
                      Quem é você?{" "}
                      <Information
                        text="O Segurado é quem possui o contrato de seguro em seu nome e está coberto pelas apólices.
                                        O Comunicante está notificando a seguradora sobre um sinistro ou evento, mas não é o titular da apólice."
                      />
                    </S.TitleForm>
                    <Tabs
                      forForms
                      tabContentItem={getTabItems()}
                      onTabChange={handleTabChange}
                    />
                  </>
                )}
              </form>
              {steps[1]?.step && (
                <>
                  <FormStepTwo
                    onUpdate={setFormData}
                    onUpdateSendDocuments={handleUpdateSendDocuments}
                    errors={methods.formState.errors}
                    comunicatorId={comunicatorId}
                    nextStep={handleNextStep}
                  />
                </>
              )}
              {steps?.every((step) => step.completed) && allDocumentsSent && (
                <CompleteFormMIP comunicatorId={comunicatorId} />
              )}
              {steps?.every((step) => step.completed) && !allDocumentsSent && (
                <DocumentPendent comunicatorId={comunicatorId} />
              )}
            </FormProvider>
          </S.ContentTabsMIP>
        </>
      ) : (
        <Response />
      )}
    </>
  );
};

export default FormMIP;
